/**
 * `/status/:type` route. Renders a static success / pending / failed / error
 * panel based on the URL segment, plus a Return to Cart link.
 *
 * The flow components navigate here after the BFF confirm / save call
 * completes; this page does not call any backend.
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';

import PageShell from '../../components/common/PageShell';
import { ERROR_MESSAGES, SUCCESS_MESSAGES, UI_CONFIG } from '../../utils/constants';
import { useQuery } from '../../utils/hooks';

const STATUS_TITLES: Record<string, string> = {
  success: 'Payment successful',
  pending: 'Payment pending',
  failed: 'Payment refused',
  error: 'Something went wrong',
};

const STATUS_DESCRIPTIONS: Record<string, string> = {
  success: SUCCESS_MESSAGES.PAYMENT_SUCCESS,
  pending: SUCCESS_MESSAGES.ORDER_RECEIVED,
  failed: ERROR_MESSAGES.PAYMENT_REFUSED,
  error: ERROR_MESSAGES.GENERAL,
};

const StatusPage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const query = useQuery();
  const key = type ?? 'error';
  // The save-card flow lands on /status/success too; `context=save-card`
  // distinguishes it so we show "card saved" copy instead of payment copy.
  const isSaveCard = key === 'success' && query.get('context') === 'save-card';
  const title = isSaveCard ? 'Card saved' : STATUS_TITLES[key] ?? STATUS_TITLES['error'];
  const description = isSaveCard
    ? SUCCESS_MESSAGES.SAVE_CARD_SUCCESS
    : STATUS_DESCRIPTIONS[key] ?? STATUS_DESCRIPTIONS['error'];

  return (
    <PageShell>
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ color: UI_CONFIG.TEXT_COLOR, mb: 2 }}>
          {title}
        </Typography>
        <Typography sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR }}>
          {description}
        </Typography>
      </Box>
    </PageShell>
  );
};

export default StatusPage;
